/**
 * The hero — `mockups/studio/client-detail.html`'s first region.
 *
 * The mockup's own comment says what this panel is for: "the one fact this record
 * exists to deliver: what the account is worth this month. The $4,350 and the $1,170
 * inside it are two views of that one fact, so they share ONE region and are
 * separated by whitespace, not by a border (P8)."
 *
 * **Nothing here is computed.** `month_total`, the estimate, the effective rate, the
 * floor and the extras total are five fields of `StudioClientDetailHeroSchema` and
 * `StudioClientDetailExtrasLedgerSchema`, printed as they arrive.
 *
 * Three figures the mockup prints have no field behind them and are not invented:
 * "$125 up on July" (no previous-period total), "up $4 on July" (no previous-period
 * rate), and "$67.28 of widget usage" (the estimate carries its lines but no
 * per-kind subtotal). The third is answered by folding the estimate's own lines,
 * where the two usage rows are named and priced; the other two are in the report.
 */

import { Link } from '@tanstack/react-router'

import type { StudioClientDetailView } from '@fareast/shared'

import { Fold } from '../../../components/portal/Disclosure.js'
import { ScrollX, Stack } from '../../../components/portal/Layout.js'
import { Region, RegionCols } from '../../../components/portal/Region.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import {
  Answer,
  DayText,
  HeroFigure,
  More,
  Muted,
  Num,
  Prose,
  Ref,
  SubFigure,
  Xs,
} from '../../../components/portal/Text.js'
import { HeroCapInfo } from '../../../components/studio/HeroCapInfo.js'
import { useFormat } from '../../../format/useFormat.js'

export type HeroSectionProps = {
  readonly detail: StudioClientDetailView
}

export function HeroSection({ detail }: HeroSectionProps) {
  const format = useFormat()
  const { hero, extras_ledger: ledger, retainer } = detail
  const estimate = hero.next_estimate

  return (
    <div className="studio-hero">
      <Region>
        <Answer>
          {format.monthLong(hero.period)} is{' '}
          {retainer === null
            ? 'everything approved this month — there is no retainer under it'
            : `the ${retainer.name} retainer plus the extras approved this month`}
          .
        </Answer>

        <RegionCols>
          <Stack>
            <div>
              <HeroCapInfo
                label={`${detail.client} · ${format.monthLong(hero.period)}, every model`}
                tip="What this account is worth this month — retainer plus extras across every billing model. The one fact this record exists to deliver."
              />
              <HeroFigure value={hero.month_total} />
            </div>

            {estimate === null ? (
              <Prose>
                <Muted>There is nothing to bill next month.</Muted>
              </Prose>
            ) : (
              <>
                <Prose>
                  It bills on <DayText value={estimate.issue_on} nowrap /> at an estimated{' '}
                  <Num value={estimate.total} /> — the retainer, the extras and this month’s widget
                  usage.
                </Prose>
                <Fold summary={`What the ${format.month(estimate.period)} estimate is made of`}>
                  <EstimateLines lines={estimate.lines} total={estimate.total} />
                </Fold>
              </>
            )}

            <Prose>
              {hero.effective_rate === null || hero.effective_rate_hours === null ? (
                <Muted>
                  No time has been logged against the retainer this month, so there is no effective
                  rate to read.
                </Muted>
              ) : (
                <>
                  Effective rate <Num value={hero.effective_rate} />
                  /hour on{' '}
                  {format.qty(hero.effective_rate_hours, {
                    one: 'hour',
                    other: 'hours',
                  })}{' '}
                  of retainer work, against a floor of <Num value={hero.rate_floor} />.
                  {/*
                  **A converted figure says so.** `formulae.md` §Effective $/hour is
                  answered in USD for every client — "so two clients are comparable,
                  which is what a column headed `$/h` promises" — and the floor is
                  `setting('billing.rate_floor')`, also USD. On a EUR account that puts
                  "$88.19/hour" three lines under "€890.00" with nothing saying they
                  are in different currencies, which reads as one of the two being
                  wrong. The test is the two fields, not the account: the rate's own
                  currency against the currency this account bills in, so an account
                  that bills in USD gets no clause and nothing here assumes which
                  currency the read model chose.
                */}
                  {hero.effective_rate.currency === detail.account.currency ? null : (
                    <>
                      {' '}
                      Both are in {hero.effective_rate.currency}, converted so every client&rsquo;s
                      $/hour uses the same yardstick: this account bills in{' '}
                      {detail.account.currency}, and the rate is normalised for comparison.
                    </>
                  )}
                </>
              )}{' '}
              {/*
              `asChild` and a router `Link`, per CONTRACT.md §Internal links: a full
              page load inside an internal portal is what that rule exists to prevent.
            */}
              <More asChild>
                <Link to="/studio">Every client compared →</Link>
              </More>
            </Prose>
          </Stack>

          <Stack>
            <div>
              <HeroCapInfo
                label={`Extras approved in ${format.month(hero.period)}, not yet billed`}
                tip="Extras approved this month that have not landed on an invoice yet. Delivered work waiting for the next bill."
              />
              <SubFigure value={ledger.total_unbilled} />
            </div>
            <Prose>
              {ledger.lines.length === 0
                ? 'Nothing was approved outside the package this month.'
                : estimate === null
                  ? 'They are approved and delivered; they land on the next invoice.'
                  : 'Delivered already. They land on the next invoice.'}
            </Prose>

            {ledger.lines.length === 0 ? null : (
              <Fold summary="The extras ledger">
                <ExtrasLedger ledger={ledger} issueOn={estimate?.issue_on ?? null} />
              </Fold>
            )}
          </Stack>
        </RegionCols>
      </Region>
    </div>
  )
}

/* ------------------------------------------------------------- the extras ledger */

function ExtrasLedger({
  ledger,
  issueOn,
}: {
  readonly ledger: StudioClientDetailView['extras_ledger']
  readonly issueOn: string | null
}) {
  return (
    <ScrollX>
      <Table>
        <THead>
          <Tr>
            <Th scope="col">Ref</Th>
            <Th scope="col">Line</Th>
            <Th scope="col">Approved</Th>
            <Th scope="col" right>
              Amount
            </Th>
          </Tr>
        </THead>
        <TBody>
          {ledger.lines.map((line) => (
            <Tr key={line.ref}>
              <Td ref>
                <Ref value={line.ref} nowrap />
              </Td>
              <Td>{line.description}</Td>
              <Td>
                <Xs>
                  <Muted>{line.approval_note ?? '—'}</Muted>
                </Xs>
              </Td>
              <Td right num>
                {/*
                  A waived line prints what it would have been, struck, and then the
                  zero it bills at — the mockup's own `REQ-116` row. `waived_amount`
                  is the field that says so; nothing here subtracts.
                */}
                {line.waived_amount === null ? null : (
                  <>
                    <Num value={line.waived_amount} strike />{' '}
                  </>
                )}
                <Num value={line.amount} />
              </Td>
            </Tr>
          ))}
          <Tr variant="total">
            <Td colSpan={3}>
              {issueOn === null ? (
                'Approved this month, not yet billed'
              ) : (
                <>
                  To bill on <DayText value={issueOn} nowrap />
                </>
              )}
            </Td>
            <Td right num>
              <Num value={ledger.total_unbilled} lg />
            </Td>
          </Tr>
        </TBody>
      </Table>
    </ScrollX>
  )
}

/* ------------------------------------------------------------------ the estimate */

/**
 * The next invoice's own lines, as `billing.estimateFor` dry-ran them.
 *
 * This is where "$67.28 of widget usage" is answered without adding anything up: the
 * two `usage` lines are on screen with their quantities and unit prices, which is the
 * fact the sentence above was reaching for.
 */
function EstimateLines({
  lines,
  total,
}: {
  readonly lines: NonNullable<StudioClientDetailView['hero']['next_estimate']>['lines']
  readonly total: NonNullable<StudioClientDetailView['hero']['next_estimate']>['total']
}) {
  return (
    <ScrollX>
      <Table>
        <THead>
          <Tr>
            <Th scope="col">Line</Th>
            <Th scope="col">Ref</Th>
            <Th scope="col" right>
              Qty
            </Th>
            <Th scope="col" right>
              Amount
            </Th>
          </Tr>
        </THead>
        <TBody>
          {lines.map((line) => (
            <Tr key={line.seq}>
              <Td>{line.description}</Td>
              <Td ref>{line.ref === null ? null : <Ref value={line.ref} nowrap />}</Td>
              <Td right num>
                <Num value={line.qty} />
              </Td>
              <Td right num>
                <Num value={line.amount} />
              </Td>
            </Tr>
          ))}
          <Tr variant="total">
            <Td colSpan={3}>Estimated</Td>
            <Td right num>
              <Num value={total} lg />
            </Td>
          </Tr>
        </TBody>
      </Table>
    </ScrollX>
  )
}
